/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useRef, useState } from 'react';

import { ErrorTypes } from '../../../../core/error';
import { FilteredGuideInteractor } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { Log } from '../../../../core/logging';
import { KeyManager } from '../../../../core/navigation';
import { useTivoDispatch } from '../../../../core/tivo/tivo-redux-wrapper';
import { setFilteredGuideApiErrorAction } from '../../../../store/guide/filteredguide/filteredguide.actions';
import { resetFilteredGuideIndexes } from '../../../../store/guide/filteredguide/filteredguide.reducers';
import { showEmptyFilteredGuideScreen } from '../../../../store/screens/screens.reducers';
import { showWhisper } from '../../../../store/whisper/whisper.reducer';
import { WhisperTypes } from '../../../Whisper/WhisperTypes';
import {
    FILTERED_GUIDE_FETCH_NUMBER,
    FILTERED_GUIDE_MAX_SHOWS_PER_PAGE,
} from '../filteredguide.constants';
import {
    mapCategoryTypeIndexToChannelFilter,
    mapChannelTypeIndexToChannelFilter,
} from '../filteredguide.utils';
import {
    getInfoAboutPageMap,
    getLastCompletedHour,
    getLimitedGuideProgramsByStartTime,
    getMappedItemsToAddToPages,
    getStartIndexFromTimestamp,
    GuidePaginationState,
    splitInitialPages,
    splitIntoPages,
} from '../FilteredGuideTable/filteredguidetable.utils';

const TAG: string = 'useFilteredGuideFetch';

const handleFilteredGuideError = (
    dispatch: Function,
    setPageState: React.Dispatch<React.SetStateAction<GuidePaginationState>>,
    error: unknown,
) => {
    Log.e(TAG, 'Error fetching filtered guide content:', error);
    dispatch(setFilteredGuideApiErrorAction(true));
    const whisperMessage = `${Locale.getString(
        LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
    )}(${ErrorTypes.FILTERED_GUIDE_PROGRAMS_APIGW_5XX_C2202})`;
    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.NO_ICON,
        }),
    );
    setPageState(() => ({
        pages: new Map(),
        currentPageIndex: 0,
        previousPageIndex: 0,
    }));
    dispatch(resetFilteredGuideIndexes());
};

export const useFilteredGuideFetch = () => {
    const dispatch = useTivoDispatch();
    const [pageState, setPageState] = useState<GuidePaginationState>({
        pages: new Map(),
        currentPageIndex: 0,
        previousPageIndex: 0,
    });
    const minStartTimestamp = useRef<number>(-Infinity);
    const maxStartTimestamp = useRef<number>(Infinity);
    const startTimestamp = useRef(getLastCompletedHour());
    const cacheRequestToken = useRef<string | undefined>(undefined);

    const fetchFilteredGuideContent = useCallback(
        async (categoryProp: string, channelsTypeProp: string, offset: number) => {
            Log.i('FilteredGuideTable', 'Fetching filtered guide content...');
            const categoryFilter = mapCategoryTypeIndexToChannelFilter(categoryProp);
            if (!categoryFilter) {
                Log.w('FilteredGuideTable', 'Invalid category filter provided.');
                return;
            }
            const tivoNetworkRequest = new FilteredGuideInteractor().fetchFilteredGuideData(
                categoryFilter,
                startTimestamp.current,
                mapChannelTypeIndexToChannelFilter(channelsTypeProp),
                offset,
                FILTERED_GUIDE_FETCH_NUMBER,
                cacheRequestToken.current,
            );

            try {
                dispatch(setFilteredGuideApiErrorAction(false));
                const response = await tivoNetworkRequest.send();

                if (!response) {
                    Log.w(
                        'FilteredGuideTable',
                        'No filtered guide programs found for the given filters.',
                    );
                    return;
                }

                setPageState((prev) => {
                    const pagesMap = new Map(prev.pages);
                    const goesToFuture = offset > 0;
                    const {
                        lastPageIndex,
                        lastPage,
                        lastPrograms,
                        existingOfferIds,
                        allTimesExceptLastIncompletePage,
                    } = getInfoAboutPageMap(pagesMap, goesToFuture);

                    const newPrograms = response.guidePrograms.filter(
                        (program) => !existingOfferIds.has(program.offerId),
                    );

                    if (newPrograms.length === 0) {
                        Log.i(
                            'FilteredGuideTable',
                            'No new programs to add to the filtered guide.',
                        );
                        return prev;
                    }

                    const shouldMerge = lastPage.length < FILTERED_GUIDE_MAX_SHOWS_PER_PAGE;
                    let combinedPrograms = newPrograms;
                    if (shouldMerge) {
                        combinedPrograms = goesToFuture
                            ? [...lastPrograms, ...newPrograms]
                            : [...newPrograms, ...lastPrograms];
                    }
                    const mapped = getMappedItemsToAddToPages(
                        allTimesExceptLastIncompletePage,
                        combinedPrograms,
                    );

                    let startPage = goesToFuture ? lastPageIndex + 1 : lastPageIndex - 1;
                    if (shouldMerge) {
                        startPage = lastPageIndex;
                    }

                    const newPages = splitIntoPages(mapped, startPage, goesToFuture);

                    newPages.forEach((value, key) => pagesMap.set(key, value));

                    return {
                        ...prev,
                        pages: pagesMap,
                    };
                });
            } catch (error) {
                handleFilteredGuideError(dispatch, setPageState, error);
            }
        },
        [dispatch],
    );

    const fetchInitialFilteredGuideContent = useCallback(
        async (
            categoryProp: string,
            channelsTypeProp: string,
            skipCurrentTimeCheck: boolean = false,
            cacheToken: string | undefined = undefined,
        ) => {
            Log.i('FilteredGuideTable', 'Fetching initial filtered guide content...');
            const categoryFilter = mapCategoryTypeIndexToChannelFilter(categoryProp);
            if (!categoryFilter) {
                Log.w(
                    'FilteredGuideTable',
                    'Invalid category filter provided for initial request.',
                );
                return;
            }
            const tivoNetworkRequest = new FilteredGuideInteractor().fetchFilteredGuideData(
                categoryFilter,
                startTimestamp.current,
                mapChannelTypeIndexToChannelFilter(channelsTypeProp),
                -FILTERED_GUIDE_FETCH_NUMBER,
                FILTERED_GUIDE_FETCH_NUMBER * 2 + FILTERED_GUIDE_MAX_SHOWS_PER_PAGE,
                cacheToken,
            );

            try {
                dispatch(setFilteredGuideApiErrorAction(false));
                const response = await tivoNetworkRequest.send();

                if (KeyManager.getInstance().isKeyIgnoring())
                    KeyManager.getInstance().stopKeyIgnoring();

                if (!response) {
                    Log.w(
                        'FilteredGuideTable',
                        'No filtered guide programs found for the given filters.',
                    );
                    return;
                }

                if (response.guidePrograms.length === 0) {
                    dispatch(showEmptyFilteredGuideScreen());
                }

                cacheRequestToken.current = response.responseToken;
                minStartTimestamp.current = response.minStartTime;
                maxStartTimestamp.current = response.maxStartTime;

                setPageState(() => {
                    const newPrograms = response.guidePrograms;
                    const mapped = getLimitedGuideProgramsByStartTime(newPrograms);

                    const startIndex = getStartIndexFromTimestamp(
                        startTimestamp.current,
                        mapped,
                        skipCurrentTimeCheck,
                    );

                    const pageZeroStartIndex = startIndex >= 0 ? startIndex : 0;

                    const beforePageZeroItems = mapped.slice(0, pageZeroStartIndex);
                    const fromPageZeroOnwards = mapped.slice(pageZeroStartIndex);

                    const pagesMap = splitInitialPages(beforePageZeroItems, fromPageZeroOnwards);

                    return {
                        currentPageIndex: 0,
                        previousPageIndex: 0,
                        pages: pagesMap,
                    };
                });
            } catch (error) {
                handleFilteredGuideError(dispatch, setPageState, error);
            }
        },
        [dispatch],
    );

    return {
        pageState,
        setPageState,
        minStartTimestamp,
        maxStartTimestamp,
        startTimestamp,
        cacheRequestToken,
        fetchFilteredGuideContent,
        fetchInitialFilteredGuideContent,
    };
};
